import React, { useState } from "react";
import { Redirect } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Checkbox from "./checkbox";
import CountrySelect from "./countryselect";
import ErrorMessage from "./errormessage";
import Form from "./form";
import Input from "./input";
import Option from "./option";
import P from "./p";
import Page from "./page";
import PageHeader from "./pageheader";
import PartnerInfoTooltip from "./partnerinfotooltip";
import Select from "./select";
import Title from "./title";
import genderCd from "./gendercd";
import { Loader, SmallDivider } from "../shared";

function PersonalInformationForm({
  application,
  mediaQuery,
  membership_price,
  countries,
  pronouns
}) {
  const nextUrl = "/apply/upload-headshot/";
  const submitUrl = "/api/applications/v4/application/personal_information/";
  const {
    submission: { personal_information, membership_price: __price }
  } = application || {
    submission: { personal_information: {}, membership_price: {} }
  };
  const price = membership_price || __price;
  const partnerFields =
    price !== undefined && price.membership_type_cd === "Partner"
      ? { member_name: "", member_email: "" }
      : {};
  const [form, setForm] = useState({
    confirm_tx: "",
    errors: null,
    password_tx: "",
    ...partnerFields,
    ...personal_information
  });
  if (price === undefined) return <Redirect to="/apply/" />;
  return (
    <Form form={form} setForm={setForm} nextUrl={nextUrl} submitUrl={submitUrl}>
      <div style={{ margin: "0 1rem 3.5rem 1rem" }}>
        <div>
          <Title style={{ textAlign: "left", marginBottom: ".5rem" }}>Identity</Title>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <Input
            type="text"
            name="first_name"
            value={form?.first_name || ""}
            maxLength="256"
            placeholder="First name"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}
          />
          <Input
            type="text"
            name="last_name"
            value={form?.last_name || ""}
            maxLength="256"
            placeholder="Last name"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}
          />
          <Input
            type="text"
            name="nickname"
            value={form?.nickname || ""}
            maxLength="256"
            placeholder="Nickname"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}
          />
          <Select
            name="gender_cd"
            label="Gender"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}
          >
            {Object.entries(genderCd).map(([key, value], idx) => (
              <Option key={idx} value={key}>
                {value}
              </Option>
            ))}
          </Select>
          <Select
            name="pronouns"
            label="Pronouns"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}
          >
            {pronouns.map((n, idx) => (
              <Option key={idx} value={n}>
                {n}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div style={{ margin: "0 1rem 2rem 1rem" }}>
        <div>
          <Title style={{ textAlign: "left", marginBottom: ".5rem" }}>Birthday</Title>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem" }}>
          <Select
            name="date_of_birth_month"
            label="Month"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 3" : "" }}
          >
            <Option value="1">January</Option>
            <Option value="2">February</Option>
            <Option value="3">March</Option>
            <Option value="4">April</Option>
            <Option value="5">May</Option>
            <Option value="6">June</Option>
            <Option value="7">July</Option>
            <Option value="8">August</Option>
            <Option value="9">September</Option>
            <Option value="10">October</Option>
            <Option value="11">November</Option>
            <Option value="12">December</Option>
          </Select>
          <Input
            type="number"
            name="date_of_birth_day"
            maxLength="2"
            placeholder="Day"
            value={form?.date_of_birth_day || ""}
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 3" : "" }}
          />
          <Input
            type="number"
            name="date_of_birth_year"
            maxLength="2"
            placeholder="Year"
            value={form?.date_of_birth_year || ""}
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 3" : "" }}
          />
        </div>
      </div>
      <div style={{ margin: "0 1rem 2rem 1rem" }}>
        <div>
          <Title style={{ textAlign: "left", marginBottom: ".5rem" }}>Home Address</Title>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem" }}>
          <Input
            type="text"
            name="home_street_tx"
            value={form?.home_street_tx || ""}
            maxLength="256"
            placeholder="Street"
            form={form}
            setForm={setForm}
            style={{ gridColumn: "span 3" }}
          />
          <Input
            type="text"
            name="home_city_tx"
            value={form?.home_city_tx || ""}
            maxLength="256"
            placeholder="City"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 3" : "" }}
          />
          <Input
            type="text"
            name="home_state_cd"
            value={form?.home_state_cd || ""}
            maxLength="2"
            placeholder="State"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 3" : "" }}
          />
          <Input
            type="text"
            name="home_postcode_tx"
            value={form?.home_postcode_tx || ""}
            maxLength="512"
            placeholder="Post Code"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 3" : "" }}
          />
          <CountrySelect
            name="home_country_cd"
            label="Country"
            form={form}
            setForm={setForm}
            style={{ gridColumn: "span 3" }}
            countries={countries}
          />
        </div>
      </div>
      <div style={{ margin: "0 1rem 2rem 1rem" }}>
        <div>
          <Title style={{ textAlign: "left", marginBottom: ".5rem" }}>Business Address</Title>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem" }}>
          <div
            style={{
              display: "grid",
              gap: "1rem",
              gridColumn: "span 3",
              gridTemplateColumns: "repeat(2, 1fr)"
            }}
          >
            <Input
              type="text"
              name="business_name_tx"
              value={form?.business_name_tx || ""}
              maxLength="512"
              placeholder="Company Name"
              form={form}
              setForm={setForm}
              style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}
            />
            <Input
              type="text"
              name="business_job_title_tx"
              value={form?.business_job_title_tx || ""}
              maxLength="512"
              placeholder="Job Title"
              form={form}
              setForm={setForm}
              style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}
            />
          </div>
          <Input
            type="text"
            name="business_street_tx"
            value={form?.business_street_tx || ""}
            maxLength="256"
            placeholder="Street"
            form={form}
            setForm={setForm}
            style={{ gridColumn: "span 3" }}
          />
          <Input
            type="text"
            name="business_city_tx"
            value={form?.business_city_tx || ""}
            maxLength="256"
            placeholder="City"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 3" : "" }}
          />
          <Input
            type="text"
            name="business_state_cd"
            value={form?.business_state_cd || ""}
            maxLength="2"
            placeholder="State"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 3" : "" }}
          />
          <Input
            type="text"
            name="business_postcode_tx"
            value={form?.business_postcode_tx || ""}
            maxLength="512"
            placeholder="Post Code"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 3" : "" }}
          />
          <CountrySelect
            name="business_country_cd"
            label="Country"
            form={form}
            setForm={setForm}
            style={{ gridColumn: "span 3" }}
            countries={countries}
          />
        </div>
      </div>
      <div style={{ margin: "0 1rem 3.5rem 1rem" }}>
        <Title style={{ textAlign: "left", marginBottom: ".5rem" }}>Contact</Title>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <Input
            type="tel"
            name="mobile_phone"
            value={form?.mobile_phone || ""}
            maxLength="64"
            placeholder="Mobile phone number"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}
          />
          <Input
            type="email"
            name="email"
            value={form?.email || ""}
            maxLength="255"
            placeholder="Email address"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}
          />
        </div>
      </div>
      {price.membership_type_cd === "Partner" && (
        <div style={{ margin: "0 1rem 3.5rem 1rem" }}>
          <Title style={{ textAlign: "left", marginBottom: ".5rem", position: "relative" }}>
            partner information for partner membership
            <PartnerInfoTooltip mediaQuery={mediaQuery} />
          </Title>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <Input
              type="text"
              name="member_name"
              maxLength="255"
              placeholder="Partner’s Full Name"
              value={form?.member_name || ""}
              form={form}
              setForm={setForm}
              style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}
            />
            <Input
              type="email"
              name="member_email"
              maxLength="255"
              placeholder="Partner’s Email Address"
              value={form?.member_email || ""}
              form={form}
              setForm={setForm}
              style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}
            />
          </div>
        </div>
      )}
      <SmallDivider style={{ marginBottom: "2rem" }} />
      <Title style={{ marginBottom: "2rem" }}>Create a password</Title>
      <div style={{ margin: "0 1rem 3.5rem 1rem" }}>
        <div
          style={{
            display: "grid",
            gap: "1rem",
            gridTemplateColumns: "1fr 1fr",
            marginBottom: "2rem"
          }}
        >
          <Input
            type="password"
            name="password_tx"
            placeholder="Password"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}
            minLength="8"
          />
          <Input
            type="password"
            name="confirm_tx"
            placeholder="Re-enter password"
            form={form}
            setForm={setForm}
            style={{ gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}
            minLength="8"
          />
        </div>
        <div>
          <Checkbox
            label={
              <span>
                I agree to the{" "}
                <a href="/privacy-policy" target="_blank" style={{ textDecoration: "underline" }}>
                  privacy policy
                </a>
              </span>
            }
            name="agree_privacy_policy"
            form={form}
            setForm={setForm}
            style={{ marginBottom: "1rem" }}
          />
        </div>
      </div>
      <P>
        Read more about the information we collect in our{" "}
        <a
          style={{ textDecoration: "underline" }}
          href="/privacy-policy/#personal-details"
          target="_blank"
        >
          Privacy Policy
        </a>
      </P>
    </Form>
  );
}

export default function PersonalInformation({ mediaQuery, page, total }) {
  const { isLoading, error, data } = useQuery(["application"]);
  const {
    membership_price,
    countries,
    pronouns,
    results: [application]
  } = data;
  return (
    <Page>
      <PageHeader title={`APPLICATION PAGE ${page} OF ${total}`} tagline="Tell us about yourself.">
        <P>
          Please fill out the form below. We will save the information as you go so you can come
          back and edit.
        </P>
      </PageHeader>
      {isLoading ? (
        <Loader loading={isLoading} />
      ) : (
        <PersonalInformationForm
          mediaQuery={mediaQuery}
          application={application}
          membership_price={membership_price}
          countries={countries}
          pronouns={pronouns}
        />
      )}
      {error ? <ErrorMessage error={error} /> : null}
    </Page>
  );
}
